import { Component, OnInit, Input, AfterViewInit, Output, EventEmitter } from '@angular/core';
import { FesBaseComponent } from '../../../shared/components/fes-base.component';
import { FormGroup } from '@angular/forms';
import { DataFieldService } from './forms/dataField.service';
import { DataFieldControlService } from './forms/dataField-control.service';
import { AppState } from '../../../core/app-state';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { PathTestService, UserService } from '../../../core/services'
import { PathTestDoc } from '../../../core/schema/path-test-schema';
import { activity_status, PathTestPoint, PathTestPointSeq } from '../../../core/models';
import { firstValueFrom, from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-path-test-point-seq-data-editor',
  templateUrl: './path-test-point-seq-data-editor.component.html',
  styleUrls: ['./path-test-point-seq-data-editor.component.scss'],
  providers: [DataFieldService, DataFieldControlService]
})
export class PathTestPointSeqDataEditorComponent extends FesBaseComponent implements OnInit, AfterViewInit {

  @Input('task') task: PathTestDoc

  @Input('testPoint') set inputTestPoint(pt: PathTestPoint) {
    if ( pt?.id != this.testPoint?.id ) {
      console.log('selected PointTestDoc changed in test editor: ' + pt.pointId)
    }
    this.testPoint = pt
  }

  @Input('seq') set inputSetSeq(seq:PathTestPointSeq) {
    this.setSeq(seq)
  }

  // to pass in the PathViewStore save function (the store manages changes for PathView component and its children)
  @Input() saveSeqDataImplFn : ( testPointId: string, seq:PathTestPointSeq, statusId: activity_status.Id ) => Promise<[PathTestPoint, PathTestPointSeq]>

  @Output('editCancelled') editCancelled$ = new EventEmitter<void>()
  @Output('editSaved') editSaved$ = new EventEmitter<void>()

  testPoint: PathTestPoint;
  seq: PathTestPointSeq;
    
  forms: any[];
  taskForm: FormGroup;

  _isEditing = false;
  isLoading = false;
  submitted = false;

  previousSeq: PathTestPointSeq;
  
  revisionDate: string;

  constructor(
    dataFieldService: DataFieldService,
    controlService: DataFieldControlService,
    appState: AppState,
    protected userService: UserService,
    private modalService: BsModalService,
    public pathTestService: PathTestService
  ) {
    super(appState)
    this.forms = dataFieldService.getFields();
    this.taskForm = controlService.toFormGroup(this.forms)
    this.taskForm.disable();
  }

  ngOnInit(): void { }

  ngAfterViewInit() { }

  ngOnDestroy(): void {
    this.destroy()
  }

  async askDiscardEdits(acceptDiscardFn: () => void) {
    let initialState = {
      onYes: acceptDiscardFn,
      question: 'Discard test point sequence changes?',
      focusOnNo: true,
      yesStyle: 'btn btn-danger'
    }
    const modalRef = this.modalService.show(ConfirmModalComponent, { initialState });
    await firstValueFrom(modalRef.onHidden);
  }

  // convenience getter for easy access to form fields
  get f() { return this.taskForm.controls; }

  set isEditing( bEditing: boolean ) {
    this._isEditing = bEditing
    bEditing ? this.taskForm?.enable() : this.taskForm?.disable()
  }

  get isEditing() {
    return this._isEditing
  }

  isModified() {
    return this._isEditing && this.taskForm?.dirty
  }

  onEditClick() {
    this.isEditing = true
  }

  onCancelClick() {
    this.isEditing = false
    this.reset()
    this.editCancelled$.emit()
  }

  reset() {
    this.taskForm.reset()
    if ( this.seq?.data ) {
      this.pathTestService.doWhenReady(() => this.loadTestPointSeqData())
    }
  }

  fillWithPrevious(group: any) {
    this.taskForm?.markAsDirty()
    let formKeys = group.forms.map(form => form.key)

    formKeys.forEach(elem => {
      this.taskForm.patchValue({
        [elem]: JSON.parse(JSON.stringify(this.previousSeq.data[elem]))
      });
    });
  }

  async loadTestPointSeqData( ignoredFields: string[] = null) {
    let data = JSON.parse(JSON.stringify(this.seq?.data))

    for (const [key, value] of Object.entries(data)) {
      if ( ignoredFields?.includes(key) ) {
        continue
      }
      this.taskForm.patchValue({
        [key]: value
      });
    }

    this.checkForPrevious()
  }

  checkForPrevious() {
    const sortByTimestamp = (a,b) => {
      const aTimeStamp = a?.data?.timestamp
      const bTimeStamp = b?.data?.timestamp
      const aTime = aTimeStamp ? new Date(aTimeStamp).getTime() : 0
      const bTime = bTimeStamp ? new Date(bTimeStamp).getTime() : 0
      return aTime - bTime
    }
    const allSeqs = this.task.testPoints.map(tp=>tp.seqs||[]).flat(1).sort( sortByTimestamp )
    console.log
    this.previousSeq = allSeqs.filter(s=>s.id!=this.seq?.id).last()
  }

  async saveData() {

    this.isEditing = false
    let formData = this.taskForm.getRawValue()

    const newTimestamp = this.pathTestService.nowTimeStamp()

    const changeFn = ( seq : PathTestPointSeq ) => {
      if ( !seq.data ) {
        seq.data = {}
      }
      let data = seq.data

      data.timestamp = newTimestamp
      data.userId = this.appState.getUser()?._id
      this.revisionDate = data.timestamp

      for (const key of Object.keys(formData)) {
        if (formData[key] == null || isNaN(formData[key])) {
          data[key] = formData[key]
        } else if (formData[key] === '') {
          data[key] = null
        } else {
          data[key] = parseInt(formData[key])
        }
      }      
      return seq;
    }

    try{
      const seqCopy = JSON.parse(JSON.stringify(this.seq))
      changeFn(seqCopy)
      await this.saveSeqDataImplFn(this.testPoint?.id,seqCopy,null)
      this.editSaved$.emit()
    } catch(err) {
      this.isEditing = true
      console.error(err)
      this.pathTestService.publishError(err, this)
    }
  }

  userName$ : Observable<string>
  
  setSeq( seq: PathTestPointSeq ) {
    console.log(`setSeq(id:${seq?.id},startTime:${seq?.startTime})`)
    const oldSeq = this.seq
    const bSameSeq = seq == oldSeq || 
        (seq?.id == oldSeq?.id && JSON.stringify(seq?.data)==JSON.stringify(seq?.data))

    if ( seq && !seq.data ) {
      seq.data = {}
    }
    this.seq = seq
    //console.log(`!seq:${!seq}, !bSameSeq:${!bSameSeq}, !this.isEditing:${!this.isEditing}, this.taskForm?pristine:${this.taskForm?.pristine}`)
    
    this.userName$ = null
    if ( seq?.userId ) {
      this.userName$ = from(this.userService.getById(seq.userId)).pipe( map(u=>u?`${u.firstName} ${u.lastName}`:''))
    } 

    if ( !seq || !bSameSeq || !this.isEditing || this.taskForm?.pristine ) {
      if ( this.isEditing ) {
        this.isEditing = false 
      } 
      this.reset()
    } else {
      let formData = this.taskForm.getRawValue()
      let modifiedFields : string[] = []
      for (const [key, value] of Object.entries(formData)) {
        const formValue = value == null ? '' : `${value}`
        const newValue = seq.data[key] == null ? '' : `${seq.data[key]}`
        if ( formValue != newValue) {
          const oldValue = oldSeq.data[key] == null ? '' : `${oldSeq.data[key]}`   
          if ( formValue != oldValue ) {
            // do not replace form value if edit was in progress on a field
            console.warn('Cannot update form field. It has been modified in editor AND from another source.'
            +  ` '${key}': form='${formValue}', new='${newValue}', old='${oldValue}'`)
            modifiedFields.push(key)
          }
        }
      }
      this.pathTestService.doWhenReady(() => this.loadTestPointSeqData(modifiedFields))
    }
  }

}
